import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import { useTrend } from '../../hooks/useTrend';
import { fmtInt, formatDate } from '../../lib/format';

const STORY_COLOR    = '#2e7d32';
const BUG_COLOR      = '#c62828';
const BUG_INT_COLOR  = '#ef6c00';

// Rolling-30-Tage-Sicht: kompakte Kacheln über der Monatsscheiben-Timeline.
// Zeigt Created/Closed der letzten 30 Tage, aufgeteilt in Stories + Bugs
// (Bugs zusätzlich Kunde/intern).
export function Last30Days() {
  const { rolling30: r } = useTrend();
  const period = `${formatDate(r.windowStart)} → ${formatDate(r.windowEnd)}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 1.5,
        py: 1,
        mb: 1,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 1 }}>
        <ScheduleIcon fontSize="small" color="action" />
        <Box>
          <Typography variant="overline" sx={{ lineHeight: 1, fontWeight: 700 }}>
            Letzte 30 Tage
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
            {period}
          </Typography>
        </Box>
      </Stack>

      {/* Created — Blockgruppe */}
      <Group
        label="📂 Created"
        total={r.createdTotal}
        rows={[
          { color: STORY_COLOR, label: 'Stories', value: r.stories.created },
          {
            color: BUG_COLOR,
            label: 'Bugs',
            value: r.bugs.created,
            subLabel: `K ${fmtInt(r.bugsCustomer.created)} · I ${fmtInt(r.bugsInternal.created)}`,
            subColor: BUG_INT_COLOR,
          },
        ]}
      />

      {/* Closed — Blockgruppe */}
      <Group
        label="✅ Closed"
        total={r.closedTotal}
        rows={[
          { color: STORY_COLOR, label: 'Stories', value: r.stories.closed },
          {
            color: BUG_COLOR,
            label: 'Bugs',
            value: r.bugs.closed,
            subLabel: `K ${fmtInt(r.bugsCustomer.closed)} · I ${fmtInt(r.bugsInternal.closed)}`,
            subColor: BUG_INT_COLOR,
          },
        ]}
      />

      <Box sx={{ flex: 1 }} />

      {/* Netto-Delta (Created − Closed): positiv = Backlog wächst */}
      <NetChip created={r.createdTotal} closed={r.closedTotal} />
    </Box>
  );
}

type GroupRow = {
  color:     string;
  label:     string;
  value:     number;
  subLabel?: string;
  subColor?: string;
};

function Group({ label, total, rows }: { label: string; total: number; rows: GroupRow[] }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ minWidth: 82 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
          {fmtInt(total)}
        </Typography>
      </Box>
      <Stack spacing={0.25}>
        {rows.map(r => (
          <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ minWidth: 44, color: 'text.primary' }}>
              {r.label}
            </Typography>
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: r.color, minWidth: 32, textAlign: 'right' }}>
              {fmtInt(r.value)}
            </Typography>
            {r.subLabel && (
              <Typography
                variant="caption"
                sx={{ fontSize: 11, color: 'text.primary', opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}
              >
                ({r.subLabel})
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function NetChip({ created, closed }: { created: number; closed: number }) {
  const net = created - closed;
  const color: 'success' | 'error' | 'default' =
    net === 0 ? 'default' : net > 0 ? 'error' : 'success';
  const sign = net > 0 ? '+' : (net < 0 ? '' : '±');
  const label = `Netto ${sign}${fmtInt(net)}`;
  const tip = net > 0
    ? 'Mehr Tickets erstellt als geschlossen → Backlog wächst'
    : net < 0
      ? 'Mehr Tickets geschlossen als erstellt → Backlog schrumpft'
      : 'Erstellt und geschlossen gleichauf';
  return (
    <Tooltip arrow title={tip}>
      <Chip
        size="small"
        color={color}
        variant={net === 0 ? 'outlined' : 'filled'}
        label={label}
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
      />
    </Tooltip>
  );
}
